import Phaser from 'phaser';
import {
  hitstopFrames,
  shakeAmplitude,
  isHeavyHit,
  isSuperHit,
  shouldPlayGibs,
  gibParticleCount,
  GIB_HITSTOP_BONUS,
  GIB_SHAKE_BONUS,
  hitSprayCount,
  koPoolWidth,
  isBladeWeaponId,
  MAX_BLOOD_DECALS,
  type BloodLevel,
} from '@/systems/HitJuice';
import type { HitSpec } from '@/systems/Combat';

/** Number of radial speed-lines drawn on a super hit. */
const SPEED_LINE_COUNT = 14;
const SPEED_LINE_INNER_R = 80;
const SPEED_LINE_OUTER_R = 320;
/** Pixels above entity centre where blood particles spawn (upper chest area). */
const PARTICLE_Y_OFFSET = 20;

/**
 * HitJuiceManager — Phaser-side adapter for per-hit impact effects.
 *
 * Wire up in StageScene.create():
 *   this.juice = new HitJuiceManager(this);
 *   this.combat.onHitConnect = (spec, x, y) => this.juice.triggerHit(spec.damage, x, y);
 *
 * In StageScene.update(), fold isInHitstop into the existing freeze check so
 * entities stop during hitstop, then call juice.tick() each frozen frame.
 *
 * bloodEnabled defaults to true; wire to an options toggle later.
 * TODO(MY-1439): expose bloodEnabled via options screen once settings UI lands.
 */
export class HitJuiceManager {
  private hitstopFramesLeft = 0;

  /** Toggle blood-spatter particles (options screen will wire this). */
  bloodEnabled = true;

  /** Blood/gore fidelity level (options screen will wire this). Defaults to normal. */
  bloodLevel: BloodLevel = 'normal';

  /** Persistent blood-pool decals currently on-screen (capped at MAX_BLOOD_DECALS). */
  private readonly _decals: Phaser.GameObjects.Rectangle[] = [];

  constructor(private readonly scene: Phaser.Scene) {}

  /** True while entities should be frozen for hitstop. */
  get isInHitstop(): boolean {
    return this.hitstopFramesLeft > 0;
  }

  /**
   * Fire all impact-juice effects for a single hit.
   *
   * @param damage     Raw damage from HitSpec
   * @param worldX     World-X of the hitbox centre
   * @param worldY     World-Y of the target body centre
   * @param opts       Optional context — pass isKO, spec, and attackerX for full effects
   */
  triggerHit(
    damage: number,
    worldX: number,
    worldY: number,
    opts?: { isKO?: boolean; spec?: HitSpec; attackerX?: number },
  ): void {
    const isKO = opts?.isKO ?? false;
    const isSuper = opts?.spec?.isSuper ?? false;
    const isFinisher = opts?.spec?.launcher ?? false;
    const gib = shouldPlayGibs(this.bloodLevel, { isKO, isSuper, isFinisher });

    this.hitstopFramesLeft = Math.max(
      this.hitstopFramesLeft,
      hitstopFrames(damage) + (gib ? GIB_HITSTOP_BONUS : 0),
    );
    this.triggerShake(damage, gib);
    if (isHeavyHit(damage)) this.triggerWhiteFlash();

    // Directional hit-spray scaled by BloodLevel and weapon type
    const bladeFx = isBladeWeaponId(opts?.spec?.weaponId);
    const sprayCount = hitSprayCount(damage, this.bloodLevel, bladeFx);
    if (sprayCount > 0) {
      const attackerX = opts?.attackerX;
      // Spray away from attacker: if attacker left → spray right (0°), else left (180°)
      const baseAngle = attackerX !== undefined && attackerX < worldX ? 0 : 180;
      this.spawnBloodParticles(worldX, worldY, sprayCount, baseAngle);
    }

    if (isSuperHit(damage)) this.triggerSpeedLines();
    if (gib) this.triggerGibs(worldX, worldY, gibParticleCount(this.bloodLevel));

    // KO blood pool — normal + gore levels
    if (isKO) {
      const poolW = koPoolWidth(damage, this.bloodLevel);
      if (poolW > 0) this.spawnKoPool(worldX, worldY, poolW);
    }
  }

  /** Advance hitstop counter one frame. Call every update() while frozen. */
  tick(): void {
    if (this.hitstopFramesLeft > 0) this.hitstopFramesLeft -= 1;
  }

  private triggerShake(damage: number, gib = false): void {
    const amp = shakeAmplitude(damage) + (gib ? GIB_SHAKE_BONUS : 0);
    if (amp <= 0) return;
    // Phaser shake intensity is normalised 0–1 relative to camera width
    this.scene.cameras.main.shake(gib ? 140 : 80, amp / this.scene.scale.width);
  }

  private triggerWhiteFlash(): void {
    // force:true interrupts any ongoing flash
    this.scene.cameras.main.flash(60, 255, 255, 255, true);
  }

  /**
   * Spawn blood-spray dots.
   * @param baseAngleDeg Direction the particles fly toward (degrees, 0 = right).
   *                     Particles spread ±40° around this angle.
   */
  private spawnBloodParticles(
    worldX: number,
    worldY: number,
    count: number,
    baseAngleDeg = 180,
  ): void {
    const cam = this.scene.cameras.main;
    const baseX = worldX - cam.scrollX;
    const baseY = worldY - cam.scrollY - PARTICLE_Y_OFFSET;
    const COLORS = [0xcc0000, 0x990000, 0xff2222];

    for (let i = 0; i < count; i++) {
      const spread = (Math.random() - 0.5) * 80; // ±40°
      const angleDeg = baseAngleDeg + spread;
      const angleRad = (angleDeg * Math.PI) / 180;
      const dist = 30 + Math.random() * 70;
      const color = COLORS[Math.floor(Math.random() * COLORS.length)];

      const dot = this.scene.add
        .rectangle(baseX + (Math.random() - 0.5) * 8, baseY, 3, 3, color)
        .setScrollFactor(0)
        .setDepth(4000);

      this.scene.tweens.add({
        targets: dot,
        x: dot.x + Math.cos(angleRad) * dist,
        y: dot.y + Math.sin(angleRad) * dist,
        alpha: 0,
        duration: 300 + Math.random() * 150,
        ease: 'Quad.easeIn',
        onComplete: () => {
          if (dot.active) dot.destroy();
        },
      });
    }
  }

  /**
   * Spawn a persistent blood-pool decal under a KO'd body.
   * Removes the oldest decal first when the cap is reached.
   */
  private spawnKoPool(worldX: number, worldY: number, poolW: number): void {
    const cam = this.scene.cameras.main;
    const baseX = worldX - cam.scrollX;
    const baseY = worldY - cam.scrollY + PARTICLE_Y_OFFSET;

    // Enforce decal cap — evict oldest
    if (this._decals.length >= MAX_BLOOD_DECALS) {
      const oldest = this._decals.shift();
      if (oldest?.active) oldest.destroy();
    }

    const pool = this.scene.add
      .rectangle(baseX, baseY, poolW, Math.round(poolW * 0.35), 0xcc0000, 0.85)
      .setScrollFactor(0)
      .setDepth(3980);

    this._decals.push(pool);

    this.scene.tweens.add({
      targets: pool,
      alpha: 0,
      duration: 2500,
      delay: 500,
      onComplete: () => {
        const idx = this._decals.indexOf(pool);
        if (idx !== -1) this._decals.splice(idx, 1);
        if (pool.active) pool.destroy();
      },
    });
  }

  private triggerSpeedLines(): void {
    const cx = this.scene.scale.width / 2;
    const cy = this.scene.scale.height / 2;
    const gfx = this.scene.add.graphics().setScrollFactor(0).setDepth(4500);
    gfx.lineStyle(1.5, 0xffffff, 0.7);

    for (let i = 0; i < SPEED_LINE_COUNT; i++) {
      const angle = ((i + 0.5) / SPEED_LINE_COUNT) * Math.PI * 2 + (Math.random() - 0.5) * 0.25;
      gfx.lineBetween(
        cx + Math.cos(angle) * SPEED_LINE_INNER_R,
        cy + Math.sin(angle) * SPEED_LINE_INNER_R,
        cx + Math.cos(angle) * SPEED_LINE_OUTER_R,
        cy + Math.sin(angle) * SPEED_LINE_OUTER_R,
      );
    }

    this.scene.tweens.add({
      targets: gfx,
      alpha: { from: 0.7, to: 0 },
      duration: 180,
      onComplete: () => {
        if (gfx.active) gfx.destroy();
      },
    });
  }

  /**
   * Over-the-top cel-gib burst — chunky coloured chunks explode outward from
   * the KO point, plus a flat splatter decal underneath.
   * Only called when bloodLevel === 'gore' and the KO was from a super/finisher.
   */
  private triggerGibs(worldX: number, worldY: number, count: number): void {
    const cam = this.scene.cameras.main;
    const baseX = worldX - cam.scrollX;
    const baseY = worldY - cam.scrollY - PARTICLE_Y_OFFSET;
    // Bright, saturated cartoon palette — cel-shaded MK-arcade energy
    const GIB_COLORS = [0xff3300, 0xcc0000, 0xff6600, 0xdd2200];

    for (let i = 0; i < count; i++) {
      const angleDeg = Math.random() * 360;
      const angleRad = (angleDeg * Math.PI) / 180;
      const dist = 40 + Math.random() * 100;
      const color = GIB_COLORS[Math.floor(Math.random() * GIB_COLORS.length)];
      // Chunky 4–8 px squares vs the 3 px dots of normal blood
      const size = 4 + Math.floor(Math.random() * 5);

      const chunk = this.scene.add
        .rectangle(baseX + (Math.random() - 0.5) * 12, baseY, size, size, color)
        .setScrollFactor(0)
        .setDepth(4000);

      this.scene.tweens.add({
        targets: chunk,
        x: chunk.x + Math.cos(angleRad) * dist,
        y: chunk.y + Math.sin(angleRad) * dist,
        alpha: 0,
        scaleX: 0.4,
        scaleY: 0.4,
        duration: 400 + Math.random() * 200,
        ease: 'Quad.easeIn',
        onComplete: () => {
          if (chunk.active) chunk.destroy();
        },
      });
    }

    // Flat splatter decal at impact point — fades out slowly
    const decal = this.scene.add
      .rectangle(baseX, baseY + PARTICLE_Y_OFFSET, 28, 10, 0xcc0000, 0.85)
      .setScrollFactor(0)
      .setDepth(3990);

    this.scene.tweens.add({
      targets: decal,
      alpha: 0,
      duration: 1400,
      delay: 200,
      onComplete: () => {
        if (decal.active) decal.destroy();
      },
    });
  }
}
